import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeading from '@/components/brand/SectionHeading';
import { Quote, ChevronLeft, ChevronRight, Play, ArrowRight } from 'lucide-react';
import { HOME } from '@/constants/testIds';
import { PrimaryButton } from '@/components/brand/Buttons';
import { useNavigate } from 'react-router-dom';
import TestimonialModal from '@/components/brand/TestimonialModal';

const EASE = [0.22, 1, 0.36, 1];

function subline(t) {
  const rc =
    t.role && t.company
      ? `${t.role} of ${t.company}`
      : t.role || t.company || '';
  if (!rc && !t.location) return '';
  if (!rc) return t.location;
  if (!t.location) return rc;
  return `${rc} · ${t.location}`;
}

/* ─────────────────────────────────────────────
   Single card — hover only, no entry animation
   (entry is handled by the slide wrapper)
───────────────────────────────────────────── */
function TestiCard({ t, idx, onOpen }) {
  const [hov, setHov] = useState(false);
  const hasVideo = !!t.video_url;

  return (
    <motion.div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      animate={{
        y: hov ? -12 : 0,
        boxShadow: hov
          ? '0 28px 60px rgba(15,26,56,0.26), 0 0 0 2.5px rgba(232,194,90,0.60)'
          : '0 4px 24px rgba(15,26,56,0.10)',
      }}
      style={{ boxShadow: '0 4px 24px rgba(15,26,56,0.10)' }}
      transition={{ duration: 0.38, ease: EASE }}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(t)}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(t)}
      aria-label={`Open full feedback from ${t.name}`}
      data-testid={`home-testimonial-card-${idx}`}
      className="relative rounded-2xl bg-white border border-white/30 p-6 cursor-pointer overflow-hidden flex flex-col h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E]/70"
    >
      {/* Gold top accent bar */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl"
        style={{
          background: 'linear-gradient(90deg, transparent, #E8C25A, transparent)',
          scaleX: 0,
          opacity: 0,
          transformOrigin: 'center',
        }}
        animate={{ scaleX: hov ? 1 : 0, opacity: hov ? 1 : 0 }}
        transition={{ duration: 0.35, ease: EASE }}
      />

      {/* Background glow */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(232,194,90,0.07), rgba(47,111,176,0.05))',
          opacity: 0,
        }}
        animate={{ opacity: hov ? 1 : 0 }}
        transition={{ duration: 0.4 }}
      />

      {/* Quote icon + video badge */}
      <div className="relative flex items-center justify-between mb-4">
        <motion.div
          animate={{ scale: hov ? 1.2 : 1, rotate: hov ? -10 : 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 22 }}
        >
          <Quote
            className="h-7 w-7 transition-colors duration-300"
            style={{ color: hov ? '#C9962E' : '#2F6FB0' }}
          />
        </motion.div>

        {hasVideo && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full transition-all duration-300"
            style={{
              background: hov
                ? 'linear-gradient(135deg, #C9962E, #E8C25A)'
                : 'rgba(201,150,46,0.12)',
              color: hov ? '#08111F' : '#C9962E',
            }}
          >
            <Play className="h-2.5 w-2.5 fill-current" />
            Video
          </span>
        )}
      </div>

      {/* Quote text */}
      <p className="relative text-sm text-navy-deep leading-relaxed line-clamp-4 flex-1">
        &ldquo;{t.quote}&rdquo;
      </p>

      {/* Animated divider */}
      <motion.div
        className="my-4 h-px"
        style={{ background: 'rgba(31,45,69,0.08)' }}
        animate={{ background: hov ? 'rgba(201,150,46,0.30)' : 'rgba(31,45,69,0.08)' }}
        transition={{ duration: 0.35 }}
      />

      {/* Author */}
      <div className="relative flex items-center gap-3 mb-4">
        {t.avatar ? (
          <motion.img
            src={t.avatar}
            alt={t.name}
            className="h-11 w-11 rounded-full object-cover ring-2 ring-white shadow-card flex-shrink-0"
            animate={{ scale: hov ? 1.06 : 1 }}
            style={{ scale: 1 }}
            transition={{ duration: 0.3, ease: EASE }}
          />
        ) : (
          <div className="h-11 w-11 rounded-full bg-sky-100 flex-shrink-0" />
        )}
        <div>
          <div className="text-sm font-semibold text-navy">{t.name}</div>
          <div className="text-[11px] text-slatebody leading-snug">{subline(t)}</div>
        </div>
      </div>

      {/* View Feedback / Watch Video — same PrimaryButton: magnetic + sheen + spring */}
      <div className="relative flex justify-center mt-auto">
        <PrimaryButton
          className="px-5 py-2 text-xs gap-1.5"
          icon={hasVideo ? <Play className="h-3 w-3 fill-current" /> : <ArrowRight className="h-3 w-3" />}
          onClick={(e) => { e.stopPropagation(); onOpen(t); }}
        >
          {hasVideo ? (
            <><span>Watch Video</span><ArrowRight className="h-3 w-3" /></>
          ) : (
            'View Feedback'
          )}
        </PrimaryButton>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   Slide variants — the whole row of 3 cards
   slides as one solid unit, left or right
───────────────────────────────────────────── */
const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '105%' : '-105%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? '105%' : '-105%',
    opacity: 0,
  }),
};

/* ─────────────────────────────────────────────
   Section
───────────────────────────────────────────── */
export default function Testimonials({ data, items }) {
  const heading = data?.heading;
  const testimonials = items || [];

  // [page, direction]  direction: +1 = next (slide left), -1 = prev (slide right)
  const [[page, direction], setPage] = useState([0, 1]);
  const [paused, setPaused] = useState(false);
  const [openTestimonial, setOpenTestimonial] = useState(null);
  const navigate = useNavigate();

  const paginate = (d) => {
    setPage(([p]) => [(p + d + testimonials.length) % testimonials.length, d]);
  };

  // Auto-advance
  useEffect(() => {
    if (paused || openTestimonial || !testimonials.length) return;
    const timer = setInterval(() => paginate(1), 5000);
    return () => clearInterval(timer);
  }, [paused, openTestimonial, testimonials.length]);

  if (!testimonials.length) return null;

  const visible = [
    testimonials[page % testimonials.length],
    testimonials[(page + 1) % testimonials.length],
    testimonials[(page + 2) % testimonials.length],
  ];

  return (
    <section
      className="py-16 relative overflow-hidden"
      style={{
        background:
          'linear-gradient(135deg, #2F6FB0 0%, #537EA8 45%, #C9962E 100%)',
      }}
    >
      <div className="container mx-auto">
        <SectionHeading title={heading} align="center" light />

        <div
          className="mt-10"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid={HOME.testimonialsCarousel}
        >
          {/* ── Carousel viewport ── */}
          <div className="overflow-hidden">
            <AnimatePresence custom={direction} mode="wait">
              <motion.div
                key={page}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.48, ease: EASE }}
                className="grid lg:grid-cols-3 gap-5 items-stretch"
              >
                {visible.map((t, idx) => (
                  <TestiCard
                    key={t.name + idx}
                    t={t}
                    idx={idx}
                    onOpen={setOpenTestimonial}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* ── Navigation dots + arrows ── */}
          <div className="mt-8 flex items-center justify-center gap-4">
            {/* Prev */}
            <button
              onClick={() => paginate(-1)}
              className="h-10 w-10 rounded-full bg-white/95 text-navy inline-flex items-center justify-center shadow-card hover:scale-110 hover:bg-white transition-transform duration-200"
              aria-label="Previous testimonials"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, dotIdx) => (
                <button
                  key={dotIdx}
                  onClick={() =>
                    setPage(([p]) => [dotIdx, dotIdx >= p ? 1 : -1])
                  }
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    dotIdx === page % testimonials.length
                      ? 'w-7 bg-white'
                      : 'w-2 bg-white/40 hover:bg-white/70'
                  }`}
                  aria-label={`Go to testimonial ${dotIdx + 1}`}
                />
              ))}
            </div>

            {/* Next */}
            <button
              onClick={() => paginate(1)}
              className="h-10 w-10 rounded-full bg-white/95 text-navy inline-flex items-center justify-center shadow-card hover:scale-110 hover:bg-white transition-transform duration-200"
              aria-label="Next testimonials"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* ── View More Feedbacks CTA ── */}
          <motion.div
            className="mt-8 flex justify-center"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5, ease: EASE }}
          >
            <PrimaryButton
              icon={<ArrowRight className="h-4 w-4" />}
              onClick={() => navigate('/reviews')}
            >
              View More Feedbacks
            </PrimaryButton>
          </motion.div>
        </div>
      </div>

      <TestimonialModal
        testimonial={openTestimonial}
        onClose={() => setOpenTestimonial(null)}
      />
    </section>
  );
}
